/**
 * Progress-bar design-store catalog. Mirrors `timerDesignsCatalog` /
 * `countdownDesignsCatalog`: a static gallery of self-contained card SVGs the
 * user can apply to the ProgressBar widget. Recreated 1:1 from the Claude
 * Design `Progress Bars.html` handoff (10 variants), cropped from the original
 * 1920×1080 scenes to a tight bar card and re-parametrised so the SAME fill
 * percentage drives both the fill geometry AND the on-bar label.
 *
 * Each design is now PURE DATA — a {@link SerializableDesign} — rather than a JS
 * `build(pct)` closure. The percentage moves geometry (fill width, the leading
 * "head" element, segmented cell counts), so the dynamic bits are expressed as:
 *  - `slots.pct` (the integer percentage shown in the label),
 *  - `computed` formulas over `pct`/`W`/`H` for derived geometry (fill width,
 *    knob/bubble/flower/heart/bokeh positions, gloss/sheen extents),
 *  - `fragments[]` (gated via `showIf`, repeated cells/blocks split into
 *    per-index entries) spliced at the `{{FRAGMENTS}}` marker.
 * The generic {@link renderDesign} turns the data + a live `pct` into the final
 * SVG; `renderProgressBarDesignSvg` is the single entry point the Store
 * thumbnail, the config picker and the live Pixi renderer all call.
 *
 * Card-SVG conventions (shared with the other catalogs):
 *  - viewBox is `0 0 cardWidth cardHeight`; NO width/height attrs (the raster
 *    injects them).
 *  - every `<defs>`/gradient/filter/`<g id>` id is prefixed with the design id
 *    so 10 cards can inline in one DOM document without id collisions.
 *  - the bar is laid out in a `translate(...)` group so the original
 *    coordinates from the handoff stay readable.
 *
 * Known caveat: the Cyberpunk (`起動`) and Anime VHS (`ロード中`) labels keep the
 * handoff's Japanese accents, but their display fonts (Orbitron / VT323) carry
 * no CJK glyphs, so those characters fall back to a system face in the raster.
 * Intentional — matches the mock; the percentage number renders correctly.
 */

import { renderDesign } from "../entities/designs/renderDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";
import type {
  DesignFragment,
  SerializableDesign,
} from "../entities/designs/serializableDesign";

export type ProgressBarDesignCategory = "progress";

/**
 * A progress-bar design. Carries the same metadata fields as the other catalog
 * designs plus the pure-data {@link SerializableDesign} payload (`widgetType` is
 * pinned to `"progressbar"`); the legacy `build`/`svg` closure is gone.
 */
export interface ProgressBarDesign extends SerializableDesign {
  category: ProgressBarDesignCategory;
  widgetType: "progressbar";
}

const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n));

/**
 * `fillW(pct, usable)` as a safe formula string: `round(usable * pct / 100)`.
 * `usable` is `trackWidth - leftInset` so the fill's leading edge lands at
 * exactly `pct%` of the track — full at 100%, matching the handoff's snapshot.
 */
const fillW = (usable: number): string => `round(${usable} * pct / 100)`;

// ── 01 · Dark Minimal ──────────────────────────────────────────────────────
// Rounded green track, glowing head + knob at the fill edge, label centered on
// the bar. Track 1000 wide; fill + head ride the same 0..1000 axis.
const darkDesign: ProgressBarDesign = {
  id: "dark",
  name: "Dark Minimal",
  subtitle: "Rounded track",
  widgetType: "progressbar",
  category: "progress",
  description:
    "Rounded green track with a glowing head and the label centered on the bar.",
  swatches: ["#1f1f29", "#3ad96b", "#5aff8a", "#f5f5f5"],
  cardWidth: 1040,
  cardHeight: 140,
  fonts: ["Inter"],
  slots: { pct: { kind: "pct" } },
  computed: {
    w: fillW(1000),
    cx: "clamp(w, 16, 980)",
  },
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="0" y="0" width="{{w}}" height="60" rx="30" fill="url(#dark-pbd-bar)"/>`,
    },
  ],
  frame: `<svg viewBox="0 0 1040 140" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="dark-pbd-bar" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#2cd86a"/><stop offset="100%" stop-color="#5aff8a"/></linearGradient>
    <filter id="dark-pbd-glow"><feGaussianBlur stdDeviation="6"/></filter>
  </defs>
  <g transform="translate(20 40)">
    <rect x="0" y="0" width="1000" height="60" rx="30" fill="#1f1f29"/>
    {{FRAGMENTS}}
    <circle cx="{{cx}}" cy="30" r="34" fill="#5aff8a" filter="url(#dark-pbd-glow)" opacity=".55"/>
    <circle cx="{{cx}}" cy="30" r="16" fill="#eafff0"/>
    <text x="500" y="41" text-anchor="middle" font-family="Inter, sans-serif" font-weight="800" font-size="30" fill="#06210f" letter-spacing="2">CARGANDO {{pct}}%</text>
  </g>
</svg>`,
};

// ── 02 · Segmented Blocks ──────────────────────────────────────────────────
// Ten cells filling left-to-right, leading cell outlined, label centered above.
// Each cell is split into three mutually-exclusive `showIf` fragments (off /
// leading / on) so the 10 rects stay in left-to-right order after splicing.
const segmentCells: DesignFragment[] = Array.from({ length: 10 }, (_, i) => {
  const x = i * 102;
  // off: filled <= i; leading: filled == i+1; on (non-leading): filled > i+1.
  return [
    {
      showIf: `filled < ${i + 1}`,
      svg: `<rect x="${x}" width="82" height="60" rx="6" fill="#23303a"/>`,
    },
    {
      showIf: `filled == ${i + 1}`,
      svg: `<rect x="${x}" width="82" height="60" rx="6" fill="url(#segment-pbd-fill)" stroke="#fff" stroke-width="2"/>`,
    },
    {
      showIf: `filled > ${i + 1}`,
      svg: `<rect x="${x}" width="82" height="60" rx="6" fill="url(#segment-pbd-fill)"/>`,
    },
  ];
}).flat();

const segmentDesign: ProgressBarDesign = {
  id: "segment",
  name: "Segmented Blocks",
  subtitle: "10-cell meter",
  widgetType: "progressbar",
  category: "progress",
  description:
    "Ten cells filling left-to-right with the timed label centered above.",
  swatches: ["#23303a", "#ffb703", "#48cae4", "#f5f5f5"],
  cardWidth: 1040,
  cardHeight: 120,
  fonts: ["Space Grotesk"],
  slots: { pct: { kind: "pct" } },
  computed: { filled: "clamp(round(pct / 10), 0, 10)" },
  fragments: segmentCells,
  frame: `<svg viewBox="0 0 1040 120" xmlns="http://www.w3.org/2000/svg">
  <defs><linearGradient id="segment-pbd-fill" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#48cae4"/><stop offset="100%" stop-color="#ffb703"/></linearGradient></defs>
  <g transform="translate(20 48)">
    <text x="500" y="-18" text-anchor="middle" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="26" fill="#f5f5f5" letter-spacing="6">CARGANDO {{pct}}%</text>
    <g>{{FRAGMENTS}}</g>
  </g>
</svg>`,
};

// ── 03 · Y2K Aqua Gel ──────────────────────────────────────────────────────
// Glossy gel capsule: scaling gloss highlight + a bubble riding the fill edge.
const y2kDesign: ProgressBarDesign = {
  id: "y2k",
  name: "Y2K Aqua Gel",
  subtitle: "Glossy capsule",
  widgetType: "progressbar",
  category: "progress",
  description: "Glossy gel capsule with the label centered on the bar.",
  swatches: ["#1c5fa0", "#1c8fff", "#5aff8a", "#ffffff"],
  cardWidth: 1040,
  cardHeight: 140,
  fonts: ["Audiowide"],
  slots: { pct: { kind: "pct" } },
  computed: {
    w: fillW(994),
    glossCx: "6 + w / 2",
    glossRx: "max(0, w / 2 - 14)",
    bubbleX: "clamp(6 + w - 40, 30, 994)",
    bubbleXm5: "clamp(6 + w - 40, 30, 994) - 5",
  },
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="6" y="6" width="{{w}}" height="52" rx="26" fill="url(#y2k-pbd-fill)" stroke="#fff" stroke-width="1.5"/>`,
    },
    {
      showIf: "glossRx > 0",
      svg: `<ellipse cx="{{glossCx}}" cy="20" rx="{{glossRx}}" ry="8" fill="#fff" opacity=".7"/>`,
    },
  ],
  frame: `<svg viewBox="0 0 1040 140" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="y2k-pbd-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#caffd6"/><stop offset="45%" stop-color="#5aff8a"/><stop offset="100%" stop-color="#2cd86a"/></linearGradient>
    <linearGradient id="y2k-pbd-track" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#9bd0ff"/><stop offset="100%" stop-color="#1c5fa0"/></linearGradient>
  </defs>
  <g transform="translate(20 38)">
    <rect x="0" y="0" width="1000" height="64" rx="32" fill="url(#y2k-pbd-track)" stroke="#fff" stroke-width="3"/>
    {{FRAGMENTS}}
    <g opacity=".8"><circle cx="{{bubbleX}}" cy="40" r="11" fill="#fff"/><circle cx="{{bubbleXm5}}" cy="36" r="3" fill="#fff"/></g>
    <text x="500" y="44" text-anchor="middle" font-family="Audiowide, sans-serif" font-size="30" fill="#fff" letter-spacing="4" stroke="#1c5fa0" stroke-width="0.6">LOADING {{pct}}%</text>
  </g>
</svg>`,
};

// ── 04 · Cyberpunk Neon ────────────────────────────────────────────────────
// Notched neon data bar with static milestone ticks; whole group glows.
const cyberDesign: ProgressBarDesign = {
  id: "cyber",
  name: "Cyberpunk Neon",
  subtitle: "Notched data bar",
  widgetType: "progressbar",
  category: "progress",
  description:
    "Notched neon fill with milestone ticks; label centered on the bar.",
  swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
  cardWidth: 1040,
  cardHeight: 140,
  fonts: ["Orbitron"],
  slots: { pct: { kind: "pct" } },
  computed: { w: fillW(990) },
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="5" y="5" width="{{w}}" height="50" fill="#00f0ff"/>`,
    },
  ],
  frame: `<svg viewBox="0 0 1040 140" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="cyber-pbd-g"><feGaussianBlur stdDeviation="2.5"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
  <g transform="translate(20 40)" filter="url(#cyber-pbd-g)">
    <polygon points="0,0 0,60 988,60 1000,48 1000,0" fill="#080010" stroke="#00f0ff" stroke-width="2"/>
    {{FRAGMENTS}}
    <g fill="#0a1014"><rect x="100" y="5" width="3" height="50"/><rect x="200" y="5" width="3" height="50"/><rect x="300" y="5" width="3" height="50"/><rect x="400" y="5" width="3" height="50"/><rect x="500" y="5" width="3" height="50"/><rect x="600" y="5" width="3" height="50"/></g>
    <g stroke="#ff2e88" stroke-width="3"><line x1="500" y1="-10" x2="500" y2="70"/><line x1="870" y1="-10" x2="870" y2="70"/></g>
    <text x="500" y="40" text-anchor="middle" font-family="Orbitron, sans-serif" font-weight="900" font-size="30" fill="#04181a" letter-spacing="6">起動 {{pct}}%</text>
  </g>
</svg>`,
};

// ── 05 · Editorial ─────────────────────────────────────────────────────────
// Hairline ruled meter on a paper plate, italic serif label above.
const editorialDesign: ProgressBarDesign = {
  id: "editorial",
  name: "Editorial",
  subtitle: "Ruled meter",
  widgetType: "progressbar",
  category: "progress",
  description:
    "Hairline ruled track with an italic label centered above the fill.",
  swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
  cardWidth: 1092,
  cardHeight: 150,
  fonts: ["Cormorant Garamond"],
  slots: { pct: { kind: "pct" } },
  computed: { w: fillW(997) },
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="3" y="3" width="{{w}}" height="24" fill="#c8a050"/>`,
    },
  ],
  frame: `<svg viewBox="0 0 1092 150" xmlns="http://www.w3.org/2000/svg">
  <g transform="translate(44 58)">
    <rect x="-24" y="-58" width="1052" height="150" rx="6" fill="#f5efe2"/>
    <text x="500" y="-22" text-anchor="middle" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="40" fill="#1a1815">Cargando · {{pct}}%</text>
    <rect x="0" y="0" width="1000" height="30" fill="none" stroke="#1a1815" stroke-width="2"/>
    {{FRAGMENTS}}
  </g>
</svg>`,
};

// ── 06 · Pixel XP Bar ──────────────────────────────────────────────────────
// Blocky RPG XP fill: 36px blocks on a 44px pitch tile up to the fill edge.
// `fillEnd = 8 + fillW`; block N starts at x = 8 + 44*N and is `min(36, fillW -
// 44*N)` wide, shown while `fillW > 44*N`. Max blocks = ceil(984/44) = 23, so
// the strip is N = 0..22 (each its own gated fragment, in x order).
const PIXEL_MAX_BLOCKS = 23;
const pixelBlocks: DesignFragment[] = Array.from(
  { length: PIXEL_MAX_BLOCKS },
  (_, n) => ({
    showIf: `fillW > ${44 * n}`,
    svg: `<rect x="${8 + 44 * n}" y="8" width="{{pbw${n}}}" height="44"/>`,
  }),
);
const pixelBlockWidths: Record<string, string> = {};
for (let n = 0; n < PIXEL_MAX_BLOCKS; n++) {
  pixelBlockWidths[`pbw${n}`] = `min(36, fillW - ${44 * n})`;
}

const pixelDesign: ProgressBarDesign = {
  id: "pixel",
  name: "Pixel XP Bar",
  subtitle: "RPG fill",
  widgetType: "progressbar",
  category: "progress",
  description: "Blocky XP fill with the label centered on the track.",
  swatches: ["#000000", "#ffb703", "#48cae4", "#ff3d4d"],
  cardWidth: 1040,
  cardHeight: 140,
  fonts: ["Press Start 2P"],
  slots: { pct: { kind: "pct" } },
  computed: { fillW: fillW(984), ...pixelBlockWidths },
  fragments: pixelBlocks,
  frame: `<svg viewBox="0 0 1040 140" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
  <g transform="translate(20 40)">
    <rect x="0" y="0" width="1000" height="60" fill="#000" stroke="#48cae4" stroke-width="4"/>
    <g fill="#ffb703">{{FRAGMENTS}}</g>
    <text x="500" y="40" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="20" fill="#0a121c">LOADING {{pct}}%</text>
  </g>
</svg>`,
};

// ── 07 · Cottagecore ───────────────────────────────────────────────────────
// Vine growth on a warm plate; the middle flower marks the fill edge.
const cottageDesign: ProgressBarDesign = {
  id: "cottage",
  name: "Cottagecore",
  subtitle: "Vine growth",
  widgetType: "progressbar",
  category: "progress",
  description:
    "Vine fill with flower markers and a centered italic label above.",
  swatches: ["#e8dcc0", "#6b4a2a", "#b85a4a", "#5a7a4a"],
  cardWidth: 1092,
  cardHeight: 150,
  fonts: ["Cormorant Garamond"],
  slots: { pct: { kind: "pct" } },
  computed: {
    w: fillW(996),
    headX: "clamp(4 + w, 40, 992)",
  },
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="4" y="4" width="{{w}}" height="24" rx="12" fill="#5a7a4a"/>`,
    },
  ],
  frame: `<svg viewBox="0 0 1092 150" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <g id="cottage-pbd-flower"><circle r="13" fill="#ff9da8"/><circle cx="-13" cy="-5" r="10" fill="#ff9da8"/><circle cx="13" cy="-5" r="10" fill="#ff9da8"/><circle cx="-8" cy="10" r="10" fill="#ff9da8"/><circle cx="8" cy="10" r="10" fill="#ff9da8"/><circle r="6" fill="#ffd060"/></g>
  </defs>
  <g transform="translate(44 58)">
    <rect x="-24" y="-58" width="1052" height="150" rx="8" fill="#f7eed5"/>
    <text x="500" y="-22" text-anchor="middle" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="38" fill="#2a1a08">creciendo · {{pct}}%</text>
    <rect x="0" y="0" width="1000" height="32" rx="16" fill="#e8dcc0" stroke="#6b4a2a" stroke-width="2"/>
    {{FRAGMENTS}}
    <path d="M4 16 q60 -12 120 0 q60 12 120 0 q60 -12 120 0 q60 12 120 0 q60 -12 72 0" stroke="#7a9a5a" stroke-width="2.5" fill="none"/>
    <use href="#cottage-pbd-flower" transform="translate(330 16) scale(0.8)"/>
    <use href="#cottage-pbd-flower" transform="translate({{headX}} 16)"/>
    <use href="#cottage-pbd-flower" transform="translate(940 16) scale(0.7)" opacity=".5"/>
  </g>
</svg>`,
};

// ── 08 · Brutalist ─────────────────────────────────────────────────────────
// Hard orange block fill, static divider ticks, condensed mono label.
const brutalistDesign: ProgressBarDesign = {
  id: "brutalist",
  name: "Brutalist",
  subtitle: "Block fill",
  widgetType: "progressbar",
  category: "progress",
  description: "Hard orange fill with the label centered on the track in mono.",
  swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
  cardWidth: 1040,
  cardHeight: 140,
  fonts: ["Anton"],
  slots: { pct: { kind: "pct" } },
  computed: { w: fillW(996) },
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="4" y="4" width="{{w}}" height="56" fill="#ff5a2a"/>`,
    },
  ],
  frame: `<svg viewBox="0 0 1040 140" xmlns="http://www.w3.org/2000/svg">
  <g transform="translate(20 38)">
    <rect x="0" y="0" width="1000" height="64" fill="#0a0a0a" stroke="#c8c2b8" stroke-width="2"/>
    {{FRAGMENTS}}
    <g stroke="#1a1a1a" stroke-width="3" opacity=".5"><line x1="500" y1="0" x2="500" y2="64"/><line x1="750" y1="0" x2="750" y2="64"/></g>
    <text x="500" y="44" text-anchor="middle" font-family="Anton, sans-serif" font-size="38" fill="#1a1a1a" letter-spacing="2">CARGANDO {{pct}}%</text>
  </g>
</svg>`,
};

// ── 09 · Anime VHS ─────────────────────────────────────────────────────────
// RGB-split pink→cyan fill with a heart marker at the fill edge.
const animeDesign: ProgressBarDesign = {
  id: "anime",
  name: "Anime VHS",
  subtitle: "ロード中",
  widgetType: "progressbar",
  category: "progress",
  description: "RGB-split pink→cyan fill with the label centered on the bar.",
  swatches: ["#1a0a2a", "#ff7ad0", "#7ad0ff", "#fff7c2"],
  cardWidth: 1040,
  cardHeight: 140,
  fonts: ["VT323"],
  slots: { pct: { kind: "pct" } },
  computed: {
    w: fillW(995),
    // Heart authored at FW=672 (path origin x=677); shift it to the live fill
    // edge, clamped so the ±3px RGB-split never overflows the card.
    shift: "clamp(5 + w - 677, -672, 290)",
  },
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="5" y="5" width="{{w}}" height="50" fill="url(#anime-pbd-fill)"/>`,
    },
    {
      showIf: "w > 0",
      svg: `<g transform="translate({{shift}} 0)"><path d="M677 30 q-12 -12 0 -24 q12 -12 24 0 q12 -12 24 0 q12 12 0 24 l-24 22 z" fill="#fff7c2"/></g>`,
    },
  ],
  frame: `<svg viewBox="0 0 1040 140" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="anime-pbd-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <linearGradient id="anime-pbd-fill" x1="0" x2="1" y1="0" y2="0"><stop offset="0%" stop-color="#ff7ad0"/><stop offset="100%" stop-color="#7ad0ff"/></linearGradient>
  </defs>
  <g transform="translate(20 40)" filter="url(#anime-pbd-rgb)">
    <rect x="0" y="0" width="1000" height="60" fill="#1a0a2a" stroke="#7ad0ff" stroke-width="3"/>
    {{FRAGMENTS}}
    <text x="500" y="40" text-anchor="middle" font-family="VT323, monospace" font-size="34" fill="#1a0a2a" letter-spacing="6">ロード中 {{pct}}%</text>
  </g>
</svg>`,
};

// ── 10 · Liquid Glass ──────────────────────────────────────────────────────
// Frosted glass track, scaling sheen over the fill, bokeh at the fill edge.
const liquidDesign: ProgressBarDesign = {
  id: "liquid",
  name: "Liquid Glass",
  subtitle: "Frosted fill",
  widgetType: "progressbar",
  category: "progress",
  description: "Frosted glass track with the label centered on the fill.",
  swatches: ["#1c3a7a", "#5aaaff", "#9ad4ff", "#ffffff"],
  cardWidth: 1040,
  cardHeight: 140,
  fonts: ["Space Grotesk"],
  slots: { pct: { kind: "pct" } },
  computed: {
    w: fillW(994),
    sheenW: "max(0, w - 12)",
    bokehCx: "clamp(6 + w, 30, 988)",
  },
  fragments: [
    {
      showIf: "w > 0",
      svg: `<rect x="6" y="6" width="{{w}}" height="48" rx="24" fill="url(#liquid-pbd-fill)"/>`,
    },
    {
      showIf: "sheenW > 0",
      svg: `<rect x="12" y="12" width="{{sheenW}}" height="14" rx="7" fill="#fff" opacity=".55"/>`,
    },
  ],
  frame: `<svg viewBox="0 0 1040 140" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="liquid-pbd-fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeeff"/><stop offset="50%" stop-color="#5aaaff"/><stop offset="100%" stop-color="#1c5aff"/></linearGradient>
    <radialGradient id="liquid-pbd-bokeh" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#9ad4ff" stop-opacity=".7"/><stop offset="100%" stop-color="#9ad4ff" stop-opacity="0"/></radialGradient>
  </defs>
  <g transform="translate(20 40)">
    <rect x="0" y="0" width="1000" height="60" rx="30" fill="rgba(150,190,255,.16)" stroke="rgba(180,210,255,.5)" stroke-width="1.5"/>
    {{FRAGMENTS}}
    <circle cx="{{bokehCx}}" cy="30" r="30" fill="url(#liquid-pbd-bokeh)"/>
    <text x="500" y="41" text-anchor="middle" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="28" fill="#eaf5ff" letter-spacing="2">CARGANDO {{pct}}%</text>
  </g>
</svg>`,
};

export const PROGRESS_BAR_DESIGNS: ProgressBarDesign[] = [
  darkDesign,
  segmentDesign,
  y2kDesign,
  cyberDesign,
  editorialDesign,
  pixelDesign,
  cottageDesign,
  brutalistDesign,
  animeDesign,
  liquidDesign,
];

/**
 * Render a design's card SVG for a fill percentage. `percent` is clamped to
 * `[0,100]` and rounded — the single integer that drives fill width, the
 * moving head element and the on-bar label — then handed to the generic
 * {@link renderDesign} as the `pct` value (also a numeric var for the geometry
 * formulas). Same signature as before; all callers stay unchanged.
 */
export function renderProgressBarDesignSvg(
  design: ProgressBarDesign,
  percent: number,
): string {
  const pct = clamp(Math.round(percent), 0, 100);
  return renderDesign(design, { values: { pct } });
}

export function getProgressBarDesign(
  id: string | undefined,
): ProgressBarDesign | undefined {
  if (!id) {
    return undefined;
  }
  const local = PROGRESS_BAR_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Not a first-party id — fall back to an installed community design so a
  // marketplace design the user applied (options.designId = a backend id)
  // renders live via the same renderProgressBarDesignSvg path. First-party
  // ids always win (checked above), so this only ever ADDS reach.
  return getInstalledDesign(id, "progressbar") as ProgressBarDesign | undefined;
}
